import { type Component, createSignal, onCleanup, onMount, Show } from "solid-js";
import { isServer } from "solid-js/web";
import { useKeyboardShortcutSuggestions } from "~/hooks/useKeyboardShortcutSuggestions";
import { lazy } from "~/lib/lazyWithReload";
import { useWire } from "~/wire";
import { hasPrimaryCommandCenter, setPendingGlobalCommandPaletteOpen } from "../activePromptEditor";

const CommandPalette = lazy(() => import("./CommandPalette").then((m) => ({ default: m.CommandPalette })));

/**
 * The app shell's claim on the command palette, for routes with no prompt bar to claim it —
 * search, library, skills, settings. This module holds no static edge to the palette: it binds
 * the shortcut, and the palette is fetched only once someone asks for it.
 *
 * The palette mounts with no prompt editor on purpose, so composer actions drop out of the
 * list and navigation and recents remain.
 */
export const GlobalCommandPalette: Component = () => {
  const wire = useWire();
  const { shortcutsActive } = useKeyboardShortcutSuggestions();
  const [requested, setRequested] = createSignal(false);

  /** False when a prompt bar owns the shortcut, or the palette is already up to answer itself. */
  const request = (): boolean => {
    if (requested() || hasPrimaryCommandCenter()) return false;
    // The chunk load outlives the keypress, so the intent waits for the mount.
    setPendingGlobalCommandPaletteOpen();
    setRequested(true);
    return true;
  };

  onMount(() => {
    if (isServer) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.repeat || !shortcutsActive()) return;
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== "k") return;
      if (e.shiftKey || e.altKey) return;
      if (!request()) return;
      e.preventDefault();
    };
    document.addEventListener("keydown", onKeyDown, true);
    onCleanup(() => document.removeEventListener("keydown", onKeyDown, true));
  });

  return (
    <Show when={requested()}>
      <CommandPalette
        canUploadAssets={() => wire.grants.canCreate("cp.assets")}
        scope={wire.chat.scope}
        plusModalPositionScope="newChat"
        surface="global"
      />
    </Show>
  );
};
